import { Controller } from "@hotwired/stimulus"
import { showToast } from "../lib/toast"
import { ping } from "../lib/ping"
import { trackEvent } from "../lib/analytics"

const CHECK_EVERY = 2500
const MAX_CHECKS = 24

// Share a walk's link: a pop-up showing the link as messaging apps will (its preview picture, title and
// description), with the phone's share sheet where there is one, and Copy link. While a new preview
// picture is being taken (status), it checks every few seconds and shows the picture once it's ready.
export default class extends Controller {
  static targets = ["dialog", "image", "pending", "shareButton", "copyButton"]
  static values = { url: String, title: String, text: String, status: String }

  connect() {
    // Phones (and some desktop browsers) can hand the link to another app; elsewhere, Copy link leads
    if (navigator.share) {
      this.shareButtonTarget.hidden = false
      this.copyButtonTarget.classList.replace("btn-primary", "btn-secondary")
    }
  }

  disconnect() {
    clearTimeout(this.timer)
  }

  open() {
    this.counted = false
    this.dialogTarget.showModal()
    if (this.hasPendingTarget && !this.checking) this.check(0)
  }

  close() {
    this.dialogTarget.close()
  }

  closed() {
    clearTimeout(this.timer)
    this.checking = false
  }

  backdropClick(event) {
    if (event.target === this.dialogTarget) this.close()
  }

  async share() {
    this.countShare("share_sheet")
    try {
      await navigator.share({ title: this.titleValue, text: this.textValue, url: this.urlValue })
    } catch (error) {
      if (error.name !== "AbortError") await this.copy()
    }
  }

  async copy() {
    this.countShare("copy_link")
    try {
      await navigator.clipboard.writeText(this.urlValue)
      showToast("Link copied", { icon: "check" })
    } catch (error) {
      // Some browsers and embedded views block the clipboard API; the older copy command still works there
      showToast(this.copyWithSelection() ? "Link copied" : "Couldn’t copy the link")
    }
  }

  // One count per share, however many of Share and Copy it takes
  countShare(method) {
    if (this.counted) return

    this.counted = true
    ping("walk_share", { token: new URL(this.urlValue, window.location.href).pathname.split("/").pop() })
    trackEvent("walk_share", { method }, this.element)
  }

  copyWithSelection() {
    const field = document.createElement("textarea")
    field.value = this.urlValue
    field.setAttribute("readonly", "")
    Object.assign(field.style, { position: "fixed", opacity: "0" })
    this.dialogTarget.appendChild(field)
    field.select()
    try {
      return document.execCommand("copy")
    } catch (error) {
      return false
    } finally {
      field.remove()
    }
  }

  async check(count) {
    this.checking = true
    try {
      const response = await fetch(this.statusValue, { headers: { Accept: "application/json" } })
      const { preview } = await response.json()
      if (preview) return this.showPreview(preview)
    } catch (error) {
      // Try again on the next check
    }

    if (count + 1 < MAX_CHECKS && this.dialogTarget.open) {
      this.timer = setTimeout(() => this.check(count + 1), CHECK_EVERY)
    } else {
      this.checking = false
      // Given up for now: the guide's card it shows is what the link will show
      if (count + 1 >= MAX_CHECKS) this.pendingTarget.remove()
    }
  }

  showPreview(url) {
    this.checking = false
    this.imageTarget.src = url
    this.pendingTarget.remove()
  }
}
